// Shared axios-error extractors for the sessions feature. SessionsIndex
// originally inlined these (and SourceViewer duplicated the second),
// so they're centralized here.

/** Pull the provider slug out of a backend ``detail`` of the form
 *  ``missing_provider_credential:<slug>``. The chat path emits this
 *  at HTTP 409 — we key on the detail string, not the status code,
 *  so it keeps working if another path surfaces the same shape.
 *  Returns null when the error is
 *  anything else (network failure, 5xx, missing-credential detail
 *  with no slug) so the caller can fall through to the generic
 *  error path. */
export function extractMissingProvider(err: unknown): string | null {
  if (typeof err !== "object" || err === null) return null;
  const response = (
    err as { response?: { status?: number; data?: { detail?: unknown } } }
  ).response;
  // Only consider structured 4xx — 5xx / network failures are
  // surfaced via the generic toast, not the provider-credential
  // Alert. The chat ``messages`` path uses 409; the 400 branch is
  // legacy-defensive for any caller emitting the same detail shape.
  const status = response?.status;
  if (status !== 400 && status !== 409) return null;
  const detail = response?.data?.detail;
  if (typeof detail !== "string") return null;
  const match = /^missing_provider_credential:(.+)$/.exec(detail);
  return match && match[1] ? match[1] : null;
}

/** Read the FastAPI ``detail`` string off a thrown axios error.
 *  Returns null when the error isn't a structured 4xx — leaves the
 *  global error toast to deal with network / 5xx noise. */
export function extractErrorDetail(err: unknown): string | null {
  if (typeof err !== "object" || err === null) return null;
  const response = (err as { response?: { data?: { detail?: unknown } } })
    .response;
  const detail = response?.data?.detail;
  return typeof detail === "string" ? detail : null;
}
